import { useEffect, useState } from 'react';
import type { WotGrade } from '@/lib/wot';

const RADIUS = 84;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const DURATION_MS = 1500;

/**
 * Radial trust-score gauge: animated gradient arc with a counting number,
 * glowing in the brand indigo.
 */
export function WotGauge({ score, grade }: { score: number; grade: WotGrade }) {
  const [offset, setOffset] = useState(CIRCUMFERENCE);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const target = CIRCUMFERENCE * (1 - score / 100);
    const arcRaf = requestAnimationFrame(() => setOffset(target));

    let countRaf = 0;
    const start = performance.now();
    const step = (t: number) => {
      const p = Math.min(1, (t - start) / DURATION_MS);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * score));
      if (p < 1) countRaf = requestAnimationFrame(step);
    };
    countRaf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(arcRaf);
      cancelAnimationFrame(countRaf);
    };
  }, [score]);

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        {/* soft halo behind the dial */}
        <div className="absolute inset-0 rounded-full bg-primary/25 blur-3xl animate-pulse-glow motion-reduce:animate-none" />
        <svg
          viewBox="0 0 200 200"
          className="relative size-60 sm:size-72 -rotate-90"
          role="img"
          aria-label={`Web of trust score: ${score} out of 100`}
        >
          <defs>
            <linearGradient id="wot-arc" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#484dd4" />
              <stop offset="100%" stopColor="#9ba1ff" />
            </linearGradient>
          </defs>
          <circle
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke="rgb(72 77 212 / 0.15)"
            strokeWidth="11"
          />
          <circle
            cx="100"
            cy="100"
            r={RADIUS}
            fill="none"
            stroke="url(#wot-arc)"
            strokeWidth="11"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            className="motion-reduce:transition-none"
            style={{
              transition: `stroke-dashoffset ${DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
              filter: 'drop-shadow(0 0 10px rgb(99 104 230 / 0.7))',
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-6xl sm:text-7xl font-bold tabular-nums leading-none">
            {display}
          </span>
          <span className="mt-1 text-sm text-muted-foreground font-mono">/ 100</span>
        </div>
      </div>

      <div className="flex flex-col items-center gap-1.5 text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/15 px-4 py-1.5">
          <span className="font-display text-xl font-bold text-glow">{grade.grade}</span>
          <span className="h-4 w-px bg-primary/50" />
          <span className="text-sm font-medium">{grade.label}</span>
        </span>
        <p className="max-w-xs text-sm text-muted-foreground leading-relaxed">{grade.blurb}</p>
      </div>
    </div>
  );
}
